import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { canonicalLink, canonicalUrl, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { serviceCityHeadScripts } from "@/lib/cityJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { getCityHub, CITY_HUBS, type CityHub } from "@/data/cityHubs";
import { CASE_STUDIES } from "@/data/caseStudies";
import hero from "@/assets/tuff-shed-awning.jpg";

import heroPicture from "@/assets/tuff-shed-awning.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
export const Route = createFileRoute("/awnings/in/$city")({
  beforeLoad: ({ params }) => {
    const hub = getCityHub(params.city);
    if (!hub) throw notFound();
  },
  loader: ({ params }) => {
    const hub = getCityHub(params.city);
    if (!hub) throw notFound();
    return { hub };
  },
  head: (ctx) => {
    const hub = getCityHub(ctx.params.city);
    if (!hub) return {};
    // Title kept ≤60 chars; leads with "Awnings in {City}" — the exact
    // long-tail term customers search.
    const title = hub.slug === "san-antonio" ? "San Antonio Awning Installation & Recovers | Texas Made Shade" : `Awnings in ${hub.name}, TX | Texas Made Shade`;
    const description = `Custom awnings in ${hub.name}, TX — welded aluminum frames + Sunbrella® canvas for storefronts, patios and entryways. Free quotes. Built in Seguin, installed across ${hub.region}.`;
    return {
      links: [canonicalLink(`/awnings/in/${hub.slug}`)],
      meta: routeMeta(ctx, {
        title,
        description,
        image: hero,
        imageAlt: "Tuff Shed storefront awning re-cover at a Texas retail location",
        socialTitle: `Custom Awnings in ${hub.name}, TX`,
        socialDescription: description,
      }),
      scripts: [
        ...serviceCityHeadScripts({
          path: `/awnings/in/${hub.slug}`,
          serviceName: "Custom Awnings",
          city: hub.name,
          region: hub.region,
          parentPath: "/awnings",
          parentName: "Awnings",
        }),
        ...serviceHeadScripts({
          path: `/awnings/in/${hub.slug}`,
          serviceName: `Custom Awnings in ${hub.name}, TX`,
          breadcrumbName: `Awnings in ${hub.name}`,
          description,
        }),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: `Custom Awnings in ${hub.name}, TX`,
            serviceType: "Awning Installation",
            url: canonicalUrl(`/awnings/in/${hub.slug}`),
            provider: { "@id": "https://texasmadeshade.com/#business" },
            areaServed: {
              "@type": "City",
              name: hub.name,
              containedInPlace: { "@type": "AdministrativeArea", name: hub.region },
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <main id="main-content" tabIndex={-1} className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-4xl font-bold text-navy-deep">City not found</h1>
      <p className="mt-4 text-navy-deep/75">
        We don't have a dedicated awning page for that city yet.{" "}
        <Link to="/awnings" className="text-red underline">See our main awnings page</Link>.
      </p>
    </main>
  ),
  component: AwningsCityPage,
});

function AwningsCityPage() {
  const { hub } = Route.useLoaderData() as { hub: CityHub };
  const nearby = hub.nearby
    .map((s) => getCityHub(s))
    .filter((c): c is CityHub => Boolean(c));

  return (
    <ServicePage
      currentPath={`/awnings/in/${hub.slug}`}
      serviceName={`Custom Awnings in ${hub.name}`}
      breadcrumbs={[
        { label: "Awnings", to: "/awnings" },
        { label: `Awnings in ${hub.name}, TX` },
      ]}
      eyebrow={`★ AWNINGS · ${hub.name.toUpperCase()}, TX ★`}
      title={hub.slug === "san-antonio" ? "Awning Installation & Replacement in San Antonio" : `Custom Awnings in ${hub.name}, TX`}
      intro={`Welded aluminum awning frames + Sunbrella® canvas, designed and installed for ${hub.name} homes and businesses. Storefront, patio, window and entry awnings — patterned on-site, fabricated in our Seguin shop, and installed by our own crew.`}
      heroImage={heroPicture}
      heroCta={{
        eyebrow: `Awning Company · Serving ${hub.name}, TX`,
        headline: `${hub.name} Awning Quote — Free On-Site Estimate`,
        subhead: `Tell us where the awning goes and we'll come measure ${hub.name} properties at no cost. We’ll discuss materials, measurements and installation before preparing your quote.`,
        primaryHref: "#lets-talk",
        primaryLabel: `Get My ${hub.name} Awning Quote`,
        phoneNumber: "+1 (830) 390-9588",
      }}
      gallerySection={hub.slug === "san-antonio" ? {
        title: "Tuff Shed Storefront Awning Re-Cover",
        intro: "A Texas retail project: the existing frame was reused, a new Sunbrella® cover was sewn in Seguin, and the logo was hand-painted. The specific installation city is not confirmed.",
        columns: 2,
        images: CASE_STUDIES.find((project) => project.slug === "tuff-shed-storefront-awning")?.images ?? [],
        relatedLinks: [{ to: "/case-studies/tuff-shed-storefront-awning", anchor: "See the Tuff Shed re-cover project" }],
      } : undefined}
      features={[
        `Custom awning installation in ${hub.name}, TX`,
        "Welded aluminum frames — engineered for Texas wind",
        "Sunbrella® and Tempotest® canvas (10-yr color-fast)",
        "Storefront, patio, window & entry awnings",
        "On-site patterning + same-crew install",
        `Serving ${hub.name} and surrounding ${hub.region}`,
      ]}
      body={[
        ...(hub.slug === "san-antonio" ? [
          { heading: "Metal Awnings & Entry Canopies", text: "Fixed metal awnings suit entries, windows, walkways and patios. Roof profile, projection, finish and attachment are selected for your building and the area you want covered.", cta: { label: "Explore Metal & Aluminum Awnings", href: "/metal-awnings" } },
          { heading: "Retractable Patio Awnings", text: "A retractable awning provides shade when extended and opens the patio when retracted. Compare manual and motorized operation, fabric choices and mounting requirements with our Solair® retractable options.", cta: { label: "Explore Retractable Awnings", href: "/retractable-awnings" } },
          { heading: "Replace the Fabric, Keep a Suitable Frame", text: "Send photos of the full awning and close-ups of the frame and attachment points. We can assess whether the existing frame is suitable for a new cover instead of a complete replacement.", cta: { label: "San Antonio Awning Re-Covers", href: "/recover-awnings/in/san-antonio" } },
          { heading: "Planning Your Awning Estimate", text: "Start with your project address, photos and approximate width and projection. We review the intended shade area, existing wall or frame, fabric or metal preferences, and any landlord or HOA requirements before confirming scope and installation details." },
        ] : []),
        {
          heading: `Why ${hub.name} Picks Texas Made Shade for Awnings`,
          text: `Most awning companies sub out fabrication or installation. We don't. Every welded frame is built in our Seguin shop, every canvas is sewn in-house, and the same crew that builds it installs it on your ${hub.name} property — so the quote you sign is the install you get.`,
        },
        {
          heading: `Storefront & Commercial Awnings in ${hub.name}`,
          text: `From downtown ${hub.name} retail and restaurants to office park entryways, we build branded storefront awnings that meet sign-code, hold up to ${hub.region.toLowerCase()} weather, and look sharp for years.`,
          cta: { label: "See Commercial Awnings", href: "/commercial-awnings" },
        },
        {
          heading: `Patio, Window & Entry Awnings for ${hub.name} Homes`,
          text: `Residential awnings for back patios, porches, west-facing windows and front entries. Choose from hundreds of Sunbrella solids, stripes and patterns — we bring fabric samples to your home for no charge.`,
          cta: { label: "See Residential Awnings", href: "/residential-awnings" },
        },
      ]}
      applications={[
        "Storefront Awnings",
        "Restaurant Canopies",
        "Patio Awnings",
        "Window Awnings",
        "Entry & Walkway Covers",
        "Metal Awnings",
        "Retractable Awnings",
        "Recover (existing frame)",
      ]}
      faqs={[
        {
          q: `Do you install awnings in ${hub.name}, TX?`,
          a: `Yes — we install custom welded awnings throughout ${hub.name} and the surrounding ${hub.region}. On-site measurements are free.`,
        },
        {
          q: `How much does an awning cost in ${hub.name}?`,
          a: `Residential window and patio awnings typically start in the low four-figures; large commercial storefront awnings vary by frame size and graphics. Every quote is written, itemized and free.`,
        },
        {
          q: "How long do your awnings last?",
          a: "Welded aluminum frames last 20+ years. Sunbrella canvas tops typically hold color and integrity 8–12 years in direct Texas sun before needing a recover.",
        },
        {
          q: `Can I match my ${hub.name} business's brand colors?`,
          a: `Yes — Sunbrella alone offers hundreds of solid colors and stripes, plus printed graphics for logos. We provide proof renderings before fabrication.`,
        },
      ]}
      cityCallouts={[
        {
          to: `/${hub.slug}` as string,
          city: hub.name,
          anchorText: `All Texas Made Shade services in ${hub.name}, TX`,
          blurb: `Browse every awning, cover, sail-shade and welding service we offer to ${hub.name}.`,
        },
        ...nearby.slice(0, 3).map((n) => ({
          to: `/awnings/in/${n.slug}` as string,
          city: n.name,
          anchorText: `Awnings in ${n.name}, TX`,
          blurb: `We also install custom awnings in nearby ${n.name} (${n.region}).`,
        })),
      ]}
      postCta={(
        <section className="border-t-4 border-navy-deep bg-white py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <div className="text-center">
              <div className="font-display text-sm tracking-[0.3em] text-red">★ MORE CITIES WE INSTALL AWNINGS IN</div>
              <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
                Custom Awnings Across Central & South Texas
              </h2>
            </div>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {CITY_HUBS.filter((c) => c.slug !== hub.slug).map((c) => (
                <li key={c.slug}>
                  <Link
                    to="/awnings/in/$city"
                    params={{ city: c.slug }}
                    className="block border-l-4 border-red bg-cream px-4 py-3 font-display text-sm font-bold uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
                  >
                    Awnings in {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    />
  );
}
